import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import './ManageContactGroupStyle.css'
import {
  ContactGroupChannel,
  IContact,
  IContactGroup,
  IContactGroupParticipant,
} from '@dataTypes/contact.type'
import {
  DataGridComponent,
  EmptyPlaceholder,
  InputField,
  SearchWithFilter,
  Button,
} from '@components/common'
import { useGlobalLoadingActions } from '@hooks'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  bulkAddContactGroupParticipant,
  bulkDeleteContactGroupParticipant,
  deleteContactGroup,
  fetchAllContactGroups,
  updateContactGroup,
} from '@store/actions'
import { ColumnModel } from '@syncfusion/ej2-react-grids'
import ActionButtons from '@components/common/ActionButtons/ActionButtons.component'
import AddContactGroupModal from '../AddContactGroup/AddContactGroupModal.component'
import { getFullName } from '@utils/fullname.util'
import { format } from 'date-fns'
import { PATIENT_STATUS } from '@constants/PatientManagement/status'
import StatusBadge, {
  StatusTypes,
} from '@components/common/StatusBadge/StatusBadge.component'
import { getContactStatusList } from '@utils/filter.util'
import { FilterSelection } from '@components/common/SearchWithFilter/SearchWithFilter.component'
import MessageCenterDeleteContactGroupModal from '@components/pages/Messages/MessageCenterDeleteContactGroupModal.component'
import MessageCenterDeleteContactGroupMemberModal from '@components/pages/Messages/MessageCenterDeleteContactGroupMemberModal.component'
import AddMemberModal from './AddMemberModal.component'
import ConfirmCancelModal from './ConfirmCancelModal.component'
import { SET_SHOW_MODAL } from '@store/reducers/messageCenter.reducer'
import { TextAlign } from '@constants/Common/GridComponent.contants'

type ManageContactGroupModalProps = {
  show: boolean
  onHide: () => void
}

export type TGroupChanges = {
  add: IContact[] | null
  remove: IContact[] | null
  name: string | null
  channel: string | null
}

const ManageContactGroupModal: React.FC<ManageContactGroupModalProps> = ({
  show,
  onHide,
}) => {
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const dispatch = useAppDispatch()
  const clinic = useAppSelector((state) => state.clinic.selectedClinic)
  const showModal = useAppSelector((state) => state.messageCenter.show)
  const contactGroups = useAppSelector((state) => state.contactGroup.data)
  const [filteredContactGroups, setFilteredContactGroups] =
    useState<IContactGroup[]>(contactGroups)
  const [selectedGroup, setSelectedGroup] = useState<IContactGroup | null>(
    contactGroups.length ? contactGroups[0] : null,
  )
  const [selectedContact, setSelectedContact] = useState<IContact | null>(null)
  const [activeTab, setActiveTab] = useState<'manage' | 'view'>('manage')
  const [members, setMembers] = useState<IContact[]>(
    contactGroups && contactGroups.length > 0 && contactGroups[0].contacts
      ? contactGroups[0].contacts
      : [],
  )
  const [filteredMembers, setFilteredMembers] = useState<IContact[]>(members)
  const [searchQuery, setSearchQuery] = useState('')
  const [contactFilters, setContactFilters] = useState<FilterSelection>({
    status: [],
    id: [],
  })
  const [groupFilters, setGroupFilters] = useState<FilterSelection>({
    platform: [],
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [contactsToBeAdded, setContactsToBeAdded] = useState<IContact[]>([])
  const [newGroupName, setNewGroupName] = useState<string | null>(null)
  const [showAddMemberModal, setShowAddMemberModal] = useState(false)
  const [showEditGroupModal, setShowEditGroupModal] = useState(false)
  const [showConfirmCancelEditListModal, setShowConfirmCancelEditListModal] =
    useState(false)
  const [finalMemberList, setFinalMemberList] = useState<TGroupChanges>({
    add: [],
    remove: [],
    name: null,
    channel: null,
  })

  const contactFilterSelectionList = useMemo(() => {
    return {
      status: getContactStatusList(selectedGroup?.contacts),
    }
  }, [selectedGroup])

  const groupFilterSelectionList = useMemo(() => {
    return {
      platform: ContactGroupChannel.map((c) => c.value),
    }
  }, [contactGroups])

  const handleContactFilterChange = (
    selectedFilters: FilterSelection,
  ): void => {
    setContactFilters({ ...contactFilters, ...selectedFilters })
  }

  const handleGroupFilterChange = (selectedFilters: FilterSelection): void => {
    setGroupFilters({ ...groupFilters, ...selectedFilters })
  }

  const handleSearch = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(event.target.value)
  }

  const handleSave = async (): Promise<void> => {
    if (selectedGroup && selectedGroup.contact_group_id) {
      if (finalMemberList.add) {
        const _newMembers: IContactGroupParticipant[] = finalMemberList.add.map(
          (m) => ({
            contact_group_id: selectedGroup.contact_group_id as string,
            contact_id: m.contact_id as string,
            created_by: '',
            updated_by: '',
          }),
        )
        try {
          await dispatch(bulkAddContactGroupParticipant(_newMembers)).unwrap()
          showToast(
            createSuccessToast('Success', 'Members added successfully.'),
          )
        } catch (err) {
          const message =
            typeof err === 'string'
              ? err
              : err && typeof err === 'object' && 'message' in err
                ? String((err as { message: unknown }).message)
                : 'Adding members failed. Please try again.'
          showToast(createFailedToast('Error', message))
        }
      }
      if (finalMemberList.remove) {
        try {
          await dispatch(
            bulkDeleteContactGroupParticipant(
              finalMemberList.remove.map(
                (m) =>
                  m.contact_group_participant
                    ?.contact_group_participant_id as string,
              ),
            ),
          ).unwrap()
          showToast(
            createSuccessToast('Success', 'Members deleted successfully.'),
          )
        } catch (err) {
          const message =
            typeof err === 'string'
              ? err
              : err && typeof err === 'object' && 'message' in err
                ? String((err as { message: unknown }).message)
                : 'Deleting members failed. Please try again.'
          showToast(createFailedToast('Error', message))
        }
      }
      if (finalMemberList.name || finalMemberList.channel) {
        try {
          await dispatch(
            updateContactGroup({
              contact_group_id: selectedGroup.contact_group_id,
              updates: {
                ...(finalMemberList.name
                  ? { group_name: finalMemberList.name }
                  : {}),
                ...(finalMemberList.channel
                  ? { channel: finalMemberList.channel }
                  : {}),
              },
            }),
          ).unwrap()
          showToast(
            createSuccessToast('Success', 'Group updated successfully.'),
          )
        } catch (err) {
          const message =
            typeof err === 'string'
              ? err
              : err && typeof err === 'object' && 'message' in err
                ? String((err as { message: unknown }).message)
                : 'Updating group failed. Please try again.'
          showToast(createFailedToast('Error', message))
        }
      }

      if (showEditGroupModal) {
        setShowEditGroupModal(false)
      } else {
        setActiveTab('view')
      }
    }
  }

  const handleConfirmEditName = async (): Promise<void> => {
    if (!newGroupName) return
    try {
      showLoading({ container: '.manage-contact-group-modal' })
      await dispatch(
        updateContactGroup({
          contact_group_id: selectedGroup?.contact_group_id as string,
          updates: { group_name: newGroupName },
        }),
      ).unwrap()
      showToast(
        createSuccessToast('Success', 'Group name updated successfully.'),
      )
    } catch (err) {
      const message =
        typeof err === 'string'
          ? err
          : err && typeof err === 'object' && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Failed to update group name. Please try again.'
      showToast(createFailedToast('Error', message))
    } finally {
      hideLoading('.manage-contact-group-modal')
    }
    setNewGroupName(null)
    setSelectedGroup(
      selectedGroup
        ? {
            ...selectedGroup,
            group_name: newGroupName,
          }
        : null,
    )
  }

  const onDeleteModalHide = (): void => {
    setShowDeleteModal(false)
    setSelectedContact(null)
  }

  const onConfirmDelete = async (): Promise<void> => {
    if (selectedContact) {
      try {
        showLoading({ container: '.manage-contact-group-modal' })
        await dispatch(
          bulkDeleteContactGroupParticipant([
            selectedContact.contact_group_participant
              ?.contact_group_participant_id as string,
          ]),
        ).unwrap()
        setMembers((prev) =>
          prev.filter((m) => m.contact_id !== selectedContact.contact_id),
        )
        showToast(createSuccessToast('Success', 'Member removed from group.'))
      } catch (err) {
        const message =
          typeof err === 'string'
            ? err
            : err && typeof err === 'object' && 'message' in err
              ? String((err as { message: unknown }).message)
              : 'Failed to remove member. Please try again.'
        showToast(createFailedToast('Error', message))
      } finally {
        hideLoading('.manage-contact-group-modal')
      }
      setShowDeleteModal(false)
      setSelectedContact(null)
      return
    }
    setShowDeleteModal(false)
    if (selectedGroup?.contact_group_id) {
      const groupId = selectedGroup.contact_group_id
      try {
        showLoading({ container: '.manage-contact-group-modal' })
        await dispatch(deleteContactGroup(groupId)).unwrap()
        showToast(
          createSuccessToast('Success', 'Contact group deleted successfully.'),
        )
        setSelectedGroup(null)
        setMembers([])
        const remaining = contactGroups.filter(
          (g) => g.contact_group_id !== groupId,
        )
        if (remaining.length > 0) {
          setSelectedGroup(remaining[0])
          setMembers(remaining[0].contacts ?? [])
        }
      } catch (err) {
        const message =
          typeof err === 'string'
            ? err
            : err && typeof err === 'object' && 'message' in err
              ? String((err as { message: unknown }).message)
              : 'Failed to delete contact group. Please try again.'
        showToast(createFailedToast('Error', message))
      } finally {
        hideLoading('.manage-contact-group-modal')
      }
      return
    }
  }

  const onConfirmAddMember = async (): Promise<void> => {
    setShowAddMemberModal(false)
    if (contactsToBeAdded.length > 0) {
      const _newMembers: IContactGroupParticipant[] = contactsToBeAdded.map(
        (m) => ({
          contact_group_id: selectedGroup?.contact_group_id as string,
          contact_id: m.contact_id as string,
          created_by: '',
          updated_by: '',
        }),
      )
      try {
        showLoading({ container: '.manage-contact-group-modal' })
        const response = await dispatch(
          bulkAddContactGroupParticipant(_newMembers),
        ).unwrap()
        showToast(createSuccessToast('Success', 'Members added successfully.'))
        setMembers([
          ...contactsToBeAdded.map((m) => {
            const participant = response.find(
              (r) => String(r.contact_id) === String(m.contact_id),
            )
            return {
              ...m,
              contact_group_participant: participant
                ? {
                    contact_group_participant_id:
                      participant.contact_group_participant_id,
                    date_time_created_at:
                      participant.date_time_created_at != null
                        ? typeof participant.date_time_created_at === 'string'
                          ? participant.date_time_created_at
                          : new Date(
                              participant.date_time_created_at,
                            ).toISOString()
                        : '',
                  }
                : undefined,
            }
          }),
          ...members,
        ])
        setContactsToBeAdded([])
      } catch (err) {
        const message =
          typeof err === 'string'
            ? err
            : err && typeof err === 'object' && 'message' in err
              ? String((err as { message: unknown }).message)
              : 'Adding members failed. Please try again.'
        showToast(createFailedToast('Error', message))
      } finally {
        hideLoading('.manage-contact-group-modal')
      }
    }
  }

  const onCancelEditList = (): void => {
    if (finalMemberList.add || finalMemberList.remove || finalMemberList.name) {
      setShowConfirmCancelEditListModal(true)
    } else {
      if (showEditGroupModal) {
        setShowEditGroupModal(false)
      } else {
        setActiveTab('view')
      }
    }
  }

  const manageGroupColumns: ColumnModel[] = useMemo(
    () => [
      {
        headerText: '',
        field: '',
        template: (props: IContactGroup) => (
          <div key={props.contact_group_id} className="group-item">
            <div className="mgcm-text-container">
              <div className="mgcm-main-text">
                <span className="mgcm-group-name">{props.group_name}</span>
                <span className={`badge ${props.channel.toLowerCase()}`}>
                  {props.channel}
                </span>
              </div>
              <span className="mgcm-member-count">
                {props.contacts?.length ?? 0} members
              </span>
            </div>
            <div className="mgcm-action-container">
              <ActionButtons
                onView={() => {
                  setActiveTab('view')
                  setSelectedGroup(props)
                  setMembers(props.contacts ?? [])
                  setSearchQuery('')
                }}
                onDelete={() => {
                  setSelectedGroup(props)
                  setShowDeleteModal(true)
                }}
                showView={true}
                showEdit={true}
                showDelete={true}
              />
            </div>
          </div>
        ),
      },
    ],
    [],
  )

  const viewListColumns: ColumnModel[] = useMemo(
    () => [
      {
        headerText: 'Contact name',
        field: '_fullname',
        width: 0.45,
      },
      {
        headerText: 'Date added',
        field: '_date_added',
        width: 0.25,
      },
      {
        headerText: 'Status',
        field: '_status',
        template: (props: { _status: StatusTypes | 'N/A' }) => (
          <span className="plain-text">
            <StatusBadge status={props._status as StatusTypes} />
          </span>
        ),
        width: 0.15,
      },
      {
        headerText: 'Action',
        field: '',
        template: (props: IContact) => (
          <ActionButtons
            showDelete={true}
            onDelete={() => {
              setSelectedContact(props)
              setShowDeleteModal(true)
            }}
          />
        ),
        width: 0.15,
        textAlign: TextAlign.Center,
      },
    ],
    [selectedGroup, filteredMembers, activeTab, contactGroups],
  )

  useEffect(() => {
    if (contactGroups.length > 0) {
      let _groups = contactGroups
      _groups = _groups.filter((g) => {
        if (groupFilters.platform.length !== 0)
          return groupFilters.platform.includes(g.channel)
        else return true
      })

      _groups = _groups.filter((g) => {
        if (searchQuery.length !== 0) {
          return g.group_name
            .toLowerCase()
            .includes(searchQuery.toLowerCase().trim())
        } else return true
      })

      setFilteredContactGroups(_groups)
    } else setFilteredContactGroups([])
  }, [searchQuery, groupFilters, contactGroups])

  useEffect(() => {
    if (members.length > 0) {
      let _members = members
      _members = _members.filter((m) => {
        if (contactFilters.status.length !== 0)
          return contactFilters.status.includes(
            m.patient?.patient_status !== undefined
              ? PATIENT_STATUS[m.patient?.patient_status]
              : ((m.provider?.work_status as StatusTypes) ?? 'N/A'),
          )
        else return true
      })

      _members = _members.filter((m) => {
        if (searchQuery.length !== 0) {
          return getFullName(m, true)
            .toLowerCase()
            .includes(searchQuery.toLowerCase().trim())
        } else return true
      })

      setFilteredMembers(_members)
    } else setFilteredMembers([])
  }, [searchQuery, contactFilters, members, contactGroups])

  useEffect(() => {
    if (selectedGroup) {
      const add =
        members.length > 0
          ? members.filter((m) => {
              return !selectedGroup.contacts?.some(
                (f) => f.contact_id === m.contact_id,
              )
            })
          : null
      const remove =
        selectedGroup.contacts && selectedGroup.contacts.length > 0
          ? selectedGroup.contacts.filter((m) => {
              return !members.some((f) => f.contact_id === m.contact_id)
            })
          : null
      setFinalMemberList({
        name: null,
        channel: null,
        add: add && add.length > 0 ? add : null,
        remove: remove && remove.length > 0 ? remove : null,
      })
    }
  }, [members, selectedGroup])

  useEffect(() => {
    if (clinic) {
      dispatch(fetchAllContactGroups())
    }
  }, [clinic])

  return (
    <>
      {(showModal.group ||
        showDeleteModal ||
        showAddMemberModal ||
        showEditGroupModal) && (
        <div className="fade modal-backdrop show mcgm-custom-backdrop z-1059" />
      )}
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        className="manage-contact-group-modal"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {activeTab === 'view' ? 'View list' : 'Manage groups'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="custom-modal-body">
          {activeTab !== 'manage' && selectedGroup && (
            <>
              {activeTab === 'view' && (
                <div className="view-list-back-container">
                  <div className="back-btn">
                    <button
                      type="button"
                      className="unstyled-button mgc_left_fill"
                      aria-label="Back to manage groups"
                      onClick={() => {
                        setActiveTab('manage')
                        setSelectedGroup(null)
                        setSearchQuery('')
                      }}
                    />
                    <span>Back to manage groups</span>
                  </div>
                </div>
              )}
              <div className="view-list-group-container">
                <div className="view-list-name-container">
                  {newGroupName ? (
                    <div className="edit-name-container">
                      <InputField
                        value={newGroupName ?? ''}
                        onChange={(e) => {
                          setNewGroupName(e.target.value)
                        }}
                      />
                      <button
                        type="button"
                        className="unstyled-button mgc_close_fill cancel-icon"
                        onClick={() => {
                          setNewGroupName(null)
                        }}
                        data-bs-toggle="tooltip"
                        title="Cancel"
                        aria-label="Cancel"
                      />
                      <button
                        type="button"
                        className={`unstyled-button mgc_check_fill save-icon${newGroupName === selectedGroup.group_name ? ' disabled' : ''}`}
                        onClick={handleConfirmEditName}
                        data-bs-toggle="tooltip"
                        title="Save"
                        aria-label="Save"
                      />
                    </div>
                  ) : (
                    <span className="view-list-name">
                      {selectedGroup.group_name}
                      <button
                        type="button"
                        className="unstyled-button mgc_pencil_fill edit-icon"
                        onClick={() => {
                          setNewGroupName(selectedGroup.group_name)
                        }}
                        data-bs-toggle="tooltip"
                        title="Edit group name"
                        aria-label="Edit group name"
                      />
                    </span>
                  )}
                  <span
                    className={`badge ${selectedGroup.channel.toLowerCase()}`}
                  >
                    {selectedGroup.channel}
                  </span>
                </div>
                <div className="filter-add-container">
                  <SearchWithFilter
                    searchIcon="mgc_search_2_line"
                    filterIcon="mgc_settings_2_line"
                    placeholder="Search"
                    searchQuery={searchQuery}
                    onChange={handleSearch}
                    className="mcgm-filter"
                    filters={contactFilterSelectionList}
                    onFilterChange={handleContactFilterChange}
                    filterLabels={{ status: 'Status' }}
                  />
                  {activeTab === 'view' && (
                    <Button
                      size="md"
                      className="button-primary"
                      onClick={() => {
                        setShowAddMemberModal(true)
                      }}
                    >
                      Add member
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}

          {activeTab !== 'manage' && selectedGroup ? (
            members.length > 0 ? (
              <div className="mcgm-datagrid-container">
                <div className="mcgm-datagrid-wrapper">
                  <DataGridComponent
                    data={filteredMembers.map((m) => ({
                      ...m,
                      _fullname: getFullName(m, true),
                      _patient_provider_id:
                        m.patient?.public_id ?? m.provider?.public_id ?? 'N/A',
                      _status:
                        m.patient?.patient_status !== undefined
                          ? PATIENT_STATUS[m.patient?.patient_status]
                          : ((m.provider?.work_status as StatusTypes) ?? 'N/A'),
                      _date_added: m.contact_group_participant
                        ?.date_time_created_at
                        ? format(
                            m.contact_group_participant.date_time_created_at,
                            'dd/MM/yyyy',
                          )
                        : null,
                    }))}
                    columns={viewListColumns}
                    allowPaging={true}
                    customCSS="view-list-grid"
                  />
                  <span className="mgcm-member-count absolute">
                    {members.length ?? 0} members
                  </span>
                </div>
              </div>
            ) : (
              <EmptyPlaceholder
                title="No members found"
                content="Add contacts to this group to organize and manage them more easily."
                icon="mgc_contacts_3_fill"
                buttonText="Add members"
                onAdd={() => {
                  setShowAddMemberModal(true)
                }}
              />
            )
          ) : (
            <>
              <div className="filter-add-group-container">
                <Button
                  size="md"
                  className="button-primary"
                  onClick={() => {
                    dispatch(SET_SHOW_MODAL({ group: true }))
                  }}
                >
                  Create group
                </Button>
                <SearchWithFilter
                  searchIcon="mgc_search_2_line"
                  filterIcon="mgc_settings_2_line"
                  placeholder="Search"
                  searchQuery={searchQuery}
                  onChange={handleSearch}
                  className="mcgm-filter"
                  filters={groupFilterSelectionList}
                  onFilterChange={handleGroupFilterChange}
                  filterLabels={{
                    platform: 'Platform',
                  }}
                />
              </div>
              {contactGroups.length > 0 ? (
                <DataGridComponent
                  data={filteredContactGroups}
                  columns={manageGroupColumns}
                  allowPaging={true}
                  customCSS="manage-group-grid"
                />
              ) : (
                <EmptyPlaceholder
                  title="No groups found"
                  content="Create a group to organize and manage your contacts."
                  icon="mgc_group_3_fill"
                  buttonText="Create group"
                  onAdd={() => {
                    dispatch(SET_SHOW_MODAL({ group: true }))
                  }}
                />
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <div className="ms-auto">
            <Button
              size="md"
              className="button-secondary me-2"
              onClick={onHide}
            >
              Close
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <MessageCenterDeleteContactGroupMemberModal
        show={showDeleteModal && Boolean(selectedContact)}
        memberName={selectedContact ? getFullName(selectedContact) : ''}
        onCancel={onDeleteModalHide}
        onDelete={onConfirmDelete}
      />

      <MessageCenterDeleteContactGroupModal
        show={showDeleteModal && !selectedContact && Boolean(selectedGroup)}
        groupName={selectedGroup?.group_name ?? ''}
        onCancel={onDeleteModalHide}
        onDelete={onConfirmDelete}
      />

      {showAddMemberModal && (
        <AddMemberModal
          show={showAddMemberModal}
          onHide={() => {
            setShowAddMemberModal(false)
          }}
          onConfirmAddMember={onConfirmAddMember}
          preselectedMembers={filteredMembers}
          contactsToBeAdded={contactsToBeAdded}
          setContactsToBeAdded={setContactsToBeAdded}
        />
      )}
      {showConfirmCancelEditListModal && (
        <ConfirmCancelModal
          show={showConfirmCancelEditListModal}
          onHide={() => {
            setShowConfirmCancelEditListModal(false)
          }}
          onCancel={() => {
            if (showEditGroupModal) {
              setShowEditGroupModal(false)
            } else {
              setActiveTab('view')
            }
            setSelectedGroup(selectedGroup)
            setMembers(selectedGroup?.contacts ?? [])
            setFilteredMembers(selectedGroup?.contacts ?? [])
            setShowConfirmCancelEditListModal(false)
            setSearchQuery('')
          }}
        />
      )}
      {showEditGroupModal && selectedGroup && (
        <AddContactGroupModal
          show={showEditGroupModal}
          onHide={() => {
            setShowEditGroupModal(false)
          }}
          initialData={selectedGroup}
          showAllContacts={true}
          onCancel={onCancelEditList}
          setFinalGroupData={setFinalMemberList}
          onSave={handleSave}
        />
      )}
    </>
  )
}

export default ManageContactGroupModal
